import { createContext, useContext, useEffect, useState } from "react";
import type { QueryObserverResult } from "react-query";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import type { ChatThreadMessage } from "@/react/types/Chat/ChatRoomMessage";
import { usePunditUserContext } from "@circle-react/contexts";
import { useToast } from "@circle-react-uikit/ToastV2";
import { isElementInViewPort } from "./helper";

interface UnreadChatThreadsContextType {
  fetchUnreadChatThreads: () => Promise<QueryObserverResult<any, any>>;
  isLoading: boolean;
  isUnreadThreadFlow: boolean;
  newBadgeIds: Set<number>;
  onEventReceive: (eventData: any) => void;
  resetNewBadgeIds: () => void;
  unreadChatThreadIds: number[];
  unreadThreadsCount: number;
}

const initialState = {
  chatThreadIds: [],
};

const UnreadChatThreadsContext = createContext<any>(initialState);
UnreadChatThreadsContext.displayName = "UnreadChatThreadsContext";

export const UnreadChatThreadsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { currentCommunityMember } = usePunditUserContext();
  const [chatThreadIds, setChatThreadIds] = useState<number[]>([]);
  const [isUnreadThreadFlow, setUnreadThreadFlow] = useState<boolean>(false);
  const [newBadgeIds, setNewBadgeIds] = useState<Set<number>>(new Set());
  const { error } = useToast();

  const { pathname } = useLocation();
  const isThreadsPage = pathname === "/threads";

  useWebSocket(
    {
      channel: "Chats::CommunityMemberThreadsChannel",
      onMessageReceive: (data: any) => {
        onEventReceive(data);
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: Boolean(currentCommunityMember?.id),
    },
    [currentCommunityMember?.id],
  );

  const { isLoading, refetch: fetchUnreadChatThreads } = useQuery(
    ["thread_messages", "unread", currentCommunityMember?.id],
    () => reactQueryGet(internalApi.chatThreads.unreadChatThreads()),
    {
      enabled: !!currentCommunityMember?.policies?.can_access_threads_tab,
      onSuccess: (data: any) => {
        setChatThreadIds(data.chat_thread_ids);
      },
      onError: (err: any) => {
        error(err.message);
      },
    },
  );

  const onEventReceive = (eventData: any) => {
    if (eventData.event === "chatThreadRead") {
      onChatThreadRead(eventData.chat_thread_id);
    } else if (eventData.event === "newMessage") {
      onNewMessage(eventData.json_message);
    }
  };

  const onChatThreadRead = (readThreadId: number) => {
    if (chatThreadIds.includes(readThreadId)) {
      const updatedChatThreadIds = chatThreadIds.filter(
        threadId => threadId !== Number(readThreadId),
      );
      setChatThreadIds(updatedChatThreadIds);
    }
  };

  const onNewMessage = (message: ChatThreadMessage) => {
    if (message?.unread_flow) {
      setUnreadThreadFlow(true);
    }
    const isSenderCurrentCommunityMember =
      message.sender?.community_member_id === currentCommunityMember?.id;
    const isNotThreadMessage = !message.chat_thread_id;
    const isLiveRoomMessage = message?.chat_room_parent_type === "Live::Room";
    const isAlreadyUnreadThread = chatThreadIds.includes(
      message.chat_thread_id,
    );

    if (
      isNotThreadMessage ||
      isSenderCurrentCommunityMember ||
      isLiveRoomMessage ||
      isAlreadyUnreadThread
    )
      return;

    if (isThreadsPage) {
      const threadId: number = message.chat_thread_id;
      const threadElement = document.getElementById(String(threadId));
      if (threadElement && isElementInViewPort(threadElement)) return;
    }
    setChatThreadIds(prevValues => [...prevValues, message.chat_thread_id]);
  };

  useEffect(() => {
    setNewBadgeIds(prev => new Set([...prev, ...chatThreadIds]));
  }, [chatThreadIds]);

  const resetNewBadgeIds = () => {
    setNewBadgeIds(new Set());
  };

  const value: UnreadChatThreadsContextType = {
    fetchUnreadChatThreads,
    unreadChatThreadIds: chatThreadIds,
    unreadThreadsCount: chatThreadIds.length,
    isLoading,
    onEventReceive,
    isUnreadThreadFlow,
    newBadgeIds,
    resetNewBadgeIds,
  };

  return (
    <UnreadChatThreadsContext.Provider value={value}>
      {children}
    </UnreadChatThreadsContext.Provider>
  );
};

export const useUnreadChatThreads = () => useContext(UnreadChatThreadsContext);
